import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Security, ClientOptions } from 'filestack-js';
import { FilestackService } from '@filestack/angular';

import { HttpService } from '../http.service';
import {
  HoldingStorageObjectReqRes,
  StorageObjectReqRes,
} from 'src/app/shared/models/StorageObjectReqRes.model';
import { HoldingDocumentsSearchOptionsRequest } from 'src/app/dashboard/shared/holding/documents/HoldingDocumentsSearchOptionsRequest.model';
import { SearchOptionsResponse } from 'src/app/shared/models/SearchOptionsResponse.model';
import { StorageObjectShareReqRes } from 'src/app/dashboard/shared/holding/documents/StorageObjectShareReqRes.model';
import { StorageObjectPermissionListReqRes } from 'src/app/dashboard/shared/holding/documents/StorageObjectPermissionListReqRes.model';
import { StorageObjectQuotaReqRes } from 'src/app/shared/models/StorageObjectQuotaReqRes.model';
import { StorageObjectPolicyReqRes } from 'src/app/shared/models/StorageObjectPolicyReqRes.model';
import { AppSettingsService } from '../app-settings.service';
import { FilePreviewDialogComponent } from 'src/app/shared/components/file-preview-dialog/file-preview-dialog.component';
import { StorageObjectSearchOptionsResponse } from 'src/app/dashboard/shared/holding/documents/StorageObjectSearchOptionsResponse.model';
import { HoldingStorageObjectSearchOptionsRequest } from 'src/app/shared/models/HoldingStorageObjectSearchOptionsRequest.model';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class StorageObjectDataService {
  protected readonly BASE_ENDPOINT = 'storage-objects';

  constructor(
    private http: HttpService,
    private filestackService: FilestackService,
    private appSettingsService: AppSettingsService,
    private dialog: MatDialog
  ) {}

  protected getSearchOptionsToQuerystring(
    options: HoldingDocumentsSearchOptionsRequest
  ): string {
    let query = `?filter=${options.filter}&sortOrder=${options.sortOrder}&pageNumber=${options.pageNumber}&pageSize=${options.pageSize}&holdingId=${options.holdingId}`;

    if (options.storageObjectParentId) {
      query += `&storageObjectParentId=${options.storageObjectParentId}`;
    }
    if (options.orderBy) {
      query += `&orderBy=${options.orderBy}`;
    }
    if (options.foldersOnly) {
      query += `&orderBy=${options.orderBy.toString().toLowerCase()}`;
    }
    if (options.searchGlobally) {
      query += `&searchGlobally=${options.searchGlobally
        .toString()
        .toLowerCase()}`;
    }
    if (options.fromDate) {
      query += `&fromDate=${options.fromDate.toISOString()}`;
    }
    if (options.toDate) {
      query += `&toDate=${options.toDate.toISOString()}`;
    }
    return query;
  }

  public getFile(
    storageObjectId: number
  ): Observable<HoldingStorageObjectReqRes> {
    return this.http
      .getTyped<HoldingStorageObjectReqRes>(
        `${this.BASE_ENDPOINT}/${storageObjectId}`
      )
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  /** Get all files and folders, icnluding the folder's content recursivly */
  public getRecursive(
    storageObjectIds: number[]
  ): Observable<HoldingStorageObjectReqRes[]> {
    return this.http.postTyped<HoldingStorageObjectReqRes[]>(`${this.BASE_ENDPOINT}/recursive`, storageObjectIds)
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  public getStorageObjects(options: HoldingStorageObjectSearchOptionsRequest) {
    const querystring = options
      ? this.getSearchOptionsToQuerystring(options)
      : '';
    return this.http
      .getTyped<StorageObjectSearchOptionsResponse>(
        this.BASE_ENDPOINT + querystring
      )
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  public getLpDocuments(options: HoldingStorageObjectSearchOptionsRequest):Observable<StorageObjectSearchOptionsResponse> {
    const querystring = options
      ? this.getSearchOptionsToQuerystring(options)
      : '';
    return this.http
      .getTyped<StorageObjectSearchOptionsResponse>(`${this.BASE_ENDPOINT}/lp-documents${querystring}`)
      .pipe(
        catchError((err) => {
          return throwError(() => err);
        })
      );
  }

  public getQuota(sessionId?: string): Observable<StorageObjectQuotaReqRes> {
    let params = new HttpParams();
    if (sessionId) {
      params = params.append('sessionId', sessionId);
    }
    return this.http
      .getTyped<StorageObjectQuotaReqRes>(`${this.BASE_ENDPOINT}/quota`, params)
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  public getFilestackPolicy(): Observable<StorageObjectPolicyReqRes> {
    return this.http
      .getTyped<StorageObjectPolicyReqRes>(`${this.BASE_ENDPOINT}/policy`)
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  createFolder(holdingId: number, model: HoldingStorageObjectReqRes) {
    const url = `${this.BASE_ENDPOINT}/folders?holdingId=${holdingId}`;
    return this.http.postTyped<HoldingStorageObjectReqRes>(url, model).pipe(
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  validateFolderName(model: HoldingStorageObjectReqRes) {
    const url = `${this.BASE_ENDPOINT}/folders?holdingId=${model.holdingId}&validateOnly=true`;
    return this.http.postTyped(url, model)
  }

  createFilesLp(model: HoldingStorageObjectReqRes[], investingEntityId: number) {
    const url = `${this.BASE_ENDPOINT}?investingEntityId=${investingEntityId}`;
    return this.http
      .postTyped<HoldingStorageObjectReqRes[]>(url, model)
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  /** The file is uploaded to fileStack directly, front side. When the upload is complete,
   * we send a post request to the API, to save and create the respective database entries.
   */
  createFiles(model: HoldingStorageObjectReqRes[]) {
    return this.http
      .postTyped<HoldingStorageObjectReqRes[]>(this.BASE_ENDPOINT, model)
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  rename(storageObjectId: number, displayName: string) {
    const url = `${this.BASE_ENDPOINT}/${storageObjectId}/rename`;
    const model = new HoldingStorageObjectReqRes();
    model.displayName = displayName;
    return this.http.putTyped<HoldingStorageObjectReqRes>(url, model).pipe(
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  delete(storageFileId: number) {
    return this.deleteMultiple([storageFileId]);
  }

  deleteMultiple(storageFileIds: number[]): Observable<any> {
    return this.http
      .postTyped<any>(`${this.BASE_ENDPOINT}/delete`, storageFileIds)
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  getAllFoldersForHolding(holdingId: number, sessionId?: string) {
    const url = `${this.BASE_ENDPOINT}/folders?holdingId=${holdingId}`;
    let params = new HttpParams();
    if (sessionId) {
      params = params.append('sessionId', sessionId);
    }
    return this.http
      .getTyped<SearchOptionsResponse<HoldingStorageObjectReqRes>>(url, params)
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  /// If destination is 0, the object will be moved to the root folder (they will have no parent)
  move(
    holdingId: number,
    storageObjectIds: number[],
    destinationFolderStorageObjectId: number
  ) {
    const url = `${this.BASE_ENDPOINT}/move/${
      destinationFolderStorageObjectId || 0
    }`;
    const model = {
      storageObjectIds,
      holdingId,
    };
    return this.http.putTyped<HoldingStorageObjectReqRes>(url, model).pipe(
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  share(model: StorageObjectShareReqRes) {
    const url = `${this.BASE_ENDPOINT}/share`;
    return this.http.putTyped<void>(url, model).pipe(
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getShareSuggestions(
    holdingId: number,
    storageObjectIds: number[]
  ): Observable<{
    matches: Array<{
      storageObjectId: number;
      investingEntityId: number | null;
    }>;
  }> {
    let parameters = { holdingId, storageObjectIds };
    return this.http.postTyped<{
      matches: Array<{
        storageObjectId: number;
        investingEntityId: number | null;
      }>;
    }>(`${this.BASE_ENDPOINT}/share/suggestions`, parameters)
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  public postShareSuggestions(
    holdingId: number,
    matches: Array<{
      storageObjectId: number;
      investingEntityId: number | null;
    }>,
    personalMessage: string,
    sendShareEmails,
    sessionId: string
  ) {
    return this.http
      .postTyped(`${this.BASE_ENDPOINT}/share/bulk`, {
        matches,
        holdingId,
        personalMessage,
        sendShareEmails,
        sessionId,
      })
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  public refreshSessionData(sessionId: string) {
    return this.http.postTyped(`${this.BASE_ENDPOINT}/session/${sessionId}/refresh`, {}).pipe(
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  // The model will have a list of all investors that were previously shared for the selected files (all or some of them).
  // There is no way of adding permissins using this method.
  // Any item on the list can be set for removing permissions (from all files), or leave unchaged.
  setPermissions(model: StorageObjectPermissionListReqRes) {
    const url = `${this.BASE_ENDPOINT}/permissions`;
    return this.http.postTyped<void>(url, model).pipe(
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  previewFile(
    storageObject: StorageObjectReqRes,
    openIn: 'dialog' | 'tab' = 'tab'
  ) {
    switch (openIn) {
      case 'tab':
        const clientOptions = {
          security: {
            policy: storageObject.filestackPolicy,
            signature: storageObject.filestackSignature,
          } as Security,
        } as ClientOptions;

        this.filestackService.init(
          this.appSettingsService.fileStackApiKey,
          clientOptions
        );
        this.filestackService.preview(storageObject.filestackHandle);
        break;
      case 'dialog':
        this.dialog
          .open(FilePreviewDialogComponent, { data: storageObject })
          .afterClosed()
          .subscribe((data) => {
            if (data && data.download) {
              this.getDownloadToken(storageObject.id).subscribe(
                () => {},
                (error) => {}
              );
            }
          });
        break;
    }
  }

  getDownloadToken(storageObjectId: number) {
    return this.http
      .getTyped<string>(
        `document/${this.BASE_ENDPOINT}/${storageObjectId}/token`
      )
      .pipe(
        tap((response) => this.downloadUrlWithToken(storageObjectId, response)),
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  downloadUrlWithToken(storageObjectId: number, token: string) {
    const downloadUrl = this.http.getFullUrl(
      `document/${this.BASE_ENDPOINT}/${storageObjectId}/${token}`
    );

    const link = document.createElement('a') as HTMLAnchorElement;
    link.href = downloadUrl;
    link.download = 'download-file';
    link.target = '_blank';
    link.dispatchEvent(new MouseEvent('click'));
  }
}
